/**
 * EPX-DMN Theme System
 *
 * Color definitions for DMN shapes, edges, and UI — intentionally harmonised
 * with epx-flow so both modelers feel like one product family (mirrors the
 * dmn-js / bpmn-js visual language from Camunda).
 *
 * A theme has two layers:
 *   1. DMN-specific keys (decision, inputData, BKM, KS, edges, accent…)
 *      consumed by the SVG shape views via getThemeStyle().
 *   2. Shared design tokens (panel.*, canvas.background, grid.color, text.fill)
 *      mirrored to the --epx-dmn-* CSS variables by applyThemeCssVars(). These
 *      are the same tokens that epx-flow themes already expose, so a theme
 *      object authored for BPMN can drive DMN's UI chrome too.
 */

export const DEFAULT_THEME = {
  // ─── DRG Nodes ────────────────────────────────────────────────────
  decision:             { stroke: '#1890ff', fill: '#e6f7ff', strokeWidth: 2 },
  inputData:            { stroke: '#64748b', fill: '#f1f5f9', strokeWidth: 2 },
  businessKnowledgeModel: { stroke: '#722ed1', fill: '#f9f0ff', strokeWidth: 2 },
  knowledgeSource:      { stroke: '#d48806', fill: '#fffbe6', strokeWidth: 2 },
  decisionService:      { stroke: '#096dd9', fill: '#e6f7ff', strokeWidth: 2 },
  textAnnotation:       { stroke: '#8c8c8c', fill: 'none',    strokeWidth: 1.5 },

  // ─── Edges ────────────────────────────────────────────────────────
  informationRequirement: { stroke: '#1890ff', strokeWidth: 1.5 },
  knowledgeRequirement:   { stroke: '#722ed1', strokeWidth: 1.5 },
  authorityRequirement:   { stroke: '#d48806', strokeWidth: 1.5 },
  association:            { stroke: '#8c8c8c', strokeWidth: 1 },

  // ─── Canvas ───────────────────────────────────────────────────────
  grid:   { color: '#e8e8e8' },
  canvas: { background: '#f5f7fa' },
  text:   { fill: '#333' },

  // ─── Accent (interactive) ─────────────────────────────────────────
  accent: {
    base: '#1890ff', hover: '#096dd9', bg: '#e6f7ff',
    border: '#bae7ff', alpha: 'rgba(24,144,255,0.2)',
  },

  // ─── Table / grid neutrals ────────────────────────────────────────
  tableGrid: {
    border: '#d0d3d8', headerBg: '#eef2f5', indexBg: '#f6f7f9', zebra: '#fafafa',
    inputBg: '#e6f7ff', inputCell: '#f5fafd',
    outputBg: '#fff7e6', outputCell: '#fffdf5',
    inputBorder: '#cbd5e1', inputBgField: '#ffffff', focusBg: '#fff9c4',
  },

  // ─── UI Panel ─────────────────────────────────────────────────────
  panel: {
    background: '#fff',
    headerBackground: '#fff',
    headerBorder: '#e2e8f0',
    text: '#0f172a',
    textSecondary: '#475569',
    textMuted: '#94a3b8',
    border: '#e2e8f0',
    inputBackground: '#fff',
    inputBorder: '#cbd5e1',
    hoverBackground: '#f8fafc',
    accentBackground: '#e6f7ff',
    accentBorder: '#bae7ff',
    accentText: '#1890ff',
  },

  // ─── Expression-type accents (consumed by overlay chrome) ─────────
  list:                 { stroke: '#52c41a', fill: '#f6ffed' },
  invocation:           { stroke: '#ff4d4f', fill: '#fff1f0' },
  relation:             { stroke: '#2f54eb', fill: '#f0f5ff' },

  // ─── FEEL syntax tokens ───────────────────────────────────────────
  feelSyntax: {
    keyword: '#0b5fad',
    literal: '#0b5fad',
    number:  '#b45309',
    string:  '#15803d',
    op:      '#c2410c',
    builtin: '#722ed1',
    punct:   '#94a3b8',
  },

  // ─── Variable chips ───────────────────────────────────────────────
  chips: {
    accentBgSoft:    'rgba(24, 144, 255, 0.12)',
    accentText:      '#0b5fad',
    accentSoftBg:    'rgba(24, 144, 255, 0.15)',
    accentSoftBorder:'rgba(24, 144, 255, 0.45)',
    unknownStroke:   '#ef4444',
    unknownSoft:     'rgba(239, 68, 68, 0.12)',
    unknownText:     '#991b1b',
  },

  // ─── Inline overlay chrome ────────────────────────────────────────
  overlay: {
    scrim:       'rgba(15, 23, 42, 0.55)',
    shadow:      'rgba(0, 0, 0, 0.30)',
    menuShadow:  'rgba(15, 23, 42, 0.18)',
    logicChipBg:    '#eef2ff',
    logicChipText:  '#3730a3',
    logicChipHover: '#e0e7ff',
  },
};

// ─── Built-in DMN palettes that mirror epx-flow's three core themes ──────
// (`default`, `dark`, `highContrast`). Custom themes (nord, ocean, …) are
// registered at runtime by the host (sfd-v2) via EpxDmn.registerTheme().

const DARK_THEME = {
  decision:               { stroke: '#4fc3ff', fill: '#0e2a3d', strokeWidth: 2 },
  inputData:              { stroke: '#26c6da', fill: '#0e2f33', strokeWidth: 2 },
  businessKnowledgeModel: { stroke: '#b388ff', fill: '#22153f', strokeWidth: 2 },
  knowledgeSource:        { stroke: '#ffb74d', fill: '#33240a', strokeWidth: 2 },
  decisionService:        { stroke: '#4fc3ff', fill: '#0e2a3d', strokeWidth: 2 },
  textAnnotation:         { stroke: '#9ca3af', fill: 'none',    strokeWidth: 1.5 },
  informationRequirement: { stroke: '#4fc3ff', strokeWidth: 1.5 },
  knowledgeRequirement:   { stroke: '#b388ff', strokeWidth: 1.5 },
  authorityRequirement:   { stroke: '#ffb74d', strokeWidth: 1.5 },
  association:            { stroke: '#9ca3af', strokeWidth: 1 },
  grid:   { color: '#273241' },
  canvas: { background: '#0f172a' },
  text:   { fill: '#e5e7eb' },
  accent: {
    base: '#4fc3ff', hover: '#22a4e0', bg: '#0e2a3d',
    border: '#1e4a6e', alpha: 'rgba(79,195,255,0.25)',
  },
  tableGrid: {
    border: '#2a3548', headerBg: '#1a2332', indexBg: '#141d2c', zebra: '#161f2f',
    inputBg: '#0e2a3d', inputCell: '#0a1f2d',
    outputBg: '#33240a', outputCell: '#241906',
    inputBorder: '#374151', inputBgField: '#0f172a', focusBg: '#3b3107',
  },
  panel: {
    background: '#1a2332',
    headerBackground: '#0f172a',
    headerBorder: '#2a3548',
    text: '#e5e7eb',
    textSecondary: '#cbd5e1',
    textMuted: '#94a3b8',
    border: '#2a3548',
    inputBackground: '#0f172a',
    inputBorder: '#374151',
    hoverBackground: '#1f2937',
    accentBackground: '#0e2a3d',
    accentBorder: '#1e4a6e',
    accentText: '#4fc3ff',
  },
  feelSyntax: {
    keyword: '#79b8ff',
    literal: '#79b8ff',
    number:  '#f0b86e',
    string:  '#7ec699',
    op:      '#ff9472',
    builtin: '#c89eff',
    punct:   '#6a7891',
  },
  chips: {
    accentBgSoft:    'rgba(79, 179, 255, 0.18)',
    accentText:      '#cfe6ff',
    accentSoftBg:    'rgba(79, 179, 255, 0.18)',
    accentSoftBorder:'rgba(79, 179, 255, 0.45)',
    unknownStroke:   '#f87171',
    unknownSoft:     'rgba(248, 113, 113, 0.18)',
    unknownText:     '#fca5a5',
  },
};

const HIGH_CONTRAST_THEME = {
  decision:               { stroke: '#000000', fill: '#ffffff', strokeWidth: 3 },
  inputData:              { stroke: '#000000', fill: '#ffffff', strokeWidth: 3 },
  businessKnowledgeModel: { stroke: '#000000', fill: '#ffffff', strokeWidth: 3 },
  knowledgeSource:        { stroke: '#000000', fill: '#ffffff', strokeWidth: 3 },
  decisionService:        { stroke: '#000000', fill: '#ffffff', strokeWidth: 3 },
  textAnnotation:         { stroke: '#000000', fill: 'none',    strokeWidth: 2 },
  informationRequirement: { stroke: '#000000', strokeWidth: 2 },
  knowledgeRequirement:   { stroke: '#000000', strokeWidth: 2 },
  authorityRequirement:   { stroke: '#000000', strokeWidth: 2 },
  association:            { stroke: '#000000', strokeWidth: 2 },
  grid:   { color: '#000000' },
  canvas: { background: '#ffffff' },
  text:   { fill: '#000000' },
  accent: {
    base: '#000000', hover: '#000000', bg: '#ffff00',
    border: '#000000', alpha: 'rgba(0,0,0,0.3)',
  },
  tableGrid: {
    border: '#000000', headerBg: '#ffff00', indexBg: '#ffffff', zebra: '#ffffff',
    inputBg: '#ffff00', inputCell: '#ffffff',
    outputBg: '#ffff00', outputCell: '#ffffff',
    inputBorder: '#000000', inputBgField: '#ffffff', focusBg: '#ffff00',
  },
  panel: {
    background: '#ffffff',
    headerBackground: '#ffff00',
    headerBorder: '#000000',
    text: '#000000',
    textSecondary: '#000000',
    textMuted: '#000000',
    border: '#000000',
    inputBackground: '#ffffff',
    inputBorder: '#000000',
    hoverBackground: '#ffff00',
    accentBackground: '#ffff00',
    accentBorder: '#000000',
    accentText: '#000000',
  },
};

export const BUILTIN_THEMES = {
  default:      DEFAULT_THEME,
  dark:         DARK_THEME,
  highContrast: HIGH_CONTRAST_THEME,
};

export function mergeTheme(custom) {
  if (!custom) return { ...DEFAULT_THEME };
  const merged = {};
  for (const key of Object.keys(DEFAULT_THEME)) {
    if (typeof DEFAULT_THEME[key] === 'object' && DEFAULT_THEME[key] !== null) {
      merged[key] = { ...DEFAULT_THEME[key], ...(custom[key] || {}) };
    } else {
      merged[key] = custom[key] !== undefined ? custom[key] : DEFAULT_THEME[key];
    }
  }
  for (const key of Object.keys(custom)) {
    if (!(key in merged) && typeof custom[key] === 'object') {
      merged[key] = { ...custom[key] };
    }
  }
  return merged;
}

/**
 * Resolve a theme object accepting both DMN-native shape and the BPMN/epx-flow
 * shape (panel/canvas/grid/text + element-keyed colors that DMN ignores). When
 * the input carries a `dmn` extension block, those keys take precedence for
 * DMN-specific colors; otherwise DMN defaults are kept.
 */
export function resolveTheme(input) {
  if (!input) return mergeTheme(null);
  // BPMN-style theme with optional `dmn` extension block
  if (input.dmn || input.startEvent || input.userTask) {
    const out = mergeTheme(input.dmn || {});
    // Always honour shared tokens from the host theme
    if (input.panel)  out.panel  = { ...out.panel,  ...input.panel };
    if (input.canvas) out.canvas = { ...out.canvas, ...input.canvas };
    if (input.grid)   out.grid   = { ...out.grid,   ...input.grid };
    if (input.text)   out.text   = { ...out.text,   ...input.text };
    return out;
  }
  return mergeTheme(input);
}

export function getThemeStyle(graphModel, elementKey) {
  const theme = (graphModel && graphModel.__epxDmnTheme) || DEFAULT_THEME;
  return theme[elementKey] || {};
}

/**
 * Mirror a resolved theme onto the --epx-dmn-* CSS variables on a host element.
 * Scoping to an element (rather than :root) keeps two DMN instances in the
 * same page from colliding on theme.
 */
export function applyThemeCssVars(targetEl, theme) {
  if (!targetEl || !theme) return;
  const set = (k, v) => { if (v != null) targetEl.style.setProperty(k, v); };

  // DRG node tokens (consumed by editor stylesheets that style HTML chrome
  // around the SVG, e.g. legend chips, palette items)
  const dec = theme.decision || {};
  const inp = theme.inputData || {};
  const bkm = theme.businessKnowledgeModel || {};
  const ks  = theme.knowledgeSource || {};
  set('--epx-dmn-decision-stroke', dec.stroke);
  set('--epx-dmn-decision-fill',   dec.fill);
  set('--epx-dmn-input-stroke',    inp.stroke);
  set('--epx-dmn-input-fill',      inp.fill);
  set('--epx-dmn-bkm-stroke',      bkm.stroke);
  set('--epx-dmn-bkm-fill',        bkm.fill);
  set('--epx-dmn-ks-stroke',       ks.stroke);
  set('--epx-dmn-ks-fill',         ks.fill);

  // Accent
  const acc = theme.accent || {};
  set('--epx-dmn-accent',        acc.base);
  set('--epx-dmn-accent-hover',  acc.hover);
  set('--epx-dmn-accent-bg',     acc.bg);
  set('--epx-dmn-accent-border', acc.border);
  set('--epx-dmn-accent-alpha',  acc.alpha);

  // Panel / surface
  const p = theme.panel || {};
  set('--epx-dmn-panel-bg',         p.background);
  set('--epx-dmn-panel-border',     p.border);
  set('--epx-dmn-panel-hover',      p.hoverBackground);
  set('--epx-dmn-panel-text',       p.text);
  set('--epx-dmn-panel-text-muted', p.textSecondary);
  set('--epx-dmn-panel-text-faint', p.textMuted);

  // Grid / table neutrals
  const tg = theme.tableGrid || {};
  set('--epx-dmn-grid-border',    tg.border    || (theme.grid && theme.grid.color));
  set('--epx-dmn-grid-header-bg', tg.headerBg);
  set('--epx-dmn-grid-index-bg',  tg.indexBg);
  set('--epx-dmn-grid-zebra',     tg.zebra);
  set('--epx-dmn-col-input-bg',    tg.inputBg);
  set('--epx-dmn-col-input-cell',  tg.inputCell);
  set('--epx-dmn-col-output-bg',   tg.outputBg);
  set('--epx-dmn-col-output-cell', tg.outputCell);
  set('--epx-dmn-input-border',    tg.inputBorder    || p.inputBorder);
  set('--epx-dmn-input-bg',        tg.inputBgField   || p.inputBackground);
  set('--epx-dmn-focus-bg',        tg.focusBg);

  // Expression-type accents (Phase A: tokens registered; Phase B will consume them)
  const lst = theme.list || {};
  const inv = theme.invocation || {};
  const rel = theme.relation || {};
  set('--epx-dmn-list-stroke',       lst.stroke);
  set('--epx-dmn-list-fill',         lst.fill);
  set('--epx-dmn-invocation-stroke', inv.stroke);
  set('--epx-dmn-invocation-fill',   inv.fill);
  set('--epx-dmn-relation-stroke',   rel.stroke);
  set('--epx-dmn-relation-fill',     rel.fill);

  // Alpha stroke variants — only forwarded if the theme provides explicit values.
  // (Themes that don't supply these keys fall back to the :root defaults.)
  set('--epx-dmn-decision-stroke-alpha',   theme.decisionStrokeAlpha);
  set('--epx-dmn-input-stroke-alpha',      theme.inputStrokeAlpha);
  set('--epx-dmn-bkm-stroke-alpha',        theme.bkmStrokeAlpha);
  set('--epx-dmn-ks-stroke-alpha',         theme.ksStrokeAlpha);
  set('--epx-dmn-list-stroke-alpha',       theme.listStrokeAlpha);
  set('--epx-dmn-invocation-stroke-alpha', theme.invocationStrokeAlpha);
  set('--epx-dmn-relation-stroke-alpha',   theme.relationStrokeAlpha);

  // FEEL syntax tokens
  const fs = theme.feelSyntax || {};
  set('--epx-dmn-feel-keyword', fs.keyword);
  set('--epx-dmn-feel-literal', fs.literal);
  set('--epx-dmn-feel-number',  fs.number);
  set('--epx-dmn-feel-string',  fs.string);
  set('--epx-dmn-feel-op',      fs.op);
  set('--epx-dmn-feel-builtin', fs.builtin);
  set('--epx-dmn-feel-punct',   fs.punct);

  // Variable chip tokens (M1 known + R2 unknown + HP1 hit policy)
  const ch = theme.chips || {};
  set('--epx-dmn-accent-bg-soft',     ch.accentBgSoft);
  set('--epx-dmn-accent-text',        ch.accentText);
  set('--epx-dmn-accent-soft-bg',     ch.accentSoftBg);
  set('--epx-dmn-accent-soft-border', ch.accentSoftBorder);
  set('--epx-dmn-unknown-stroke',     ch.unknownStroke);
  set('--epx-dmn-unknown-soft',       ch.unknownSoft);
  set('--epx-dmn-unknown-text',       ch.unknownText);

  // Column tinting (theme can override via tableGrid block — backwards compatible)
  set('--epx-dmn-col-input-header-bg', tg.inputBg);
  set('--epx-dmn-col-input-cell-bg',   tg.inputCell);
  set('--epx-dmn-col-output-header-bg',tg.outputBg);
  set('--epx-dmn-col-output-cell-bg',  tg.outputCell);

  // Inline overlay chrome
  const ov = theme.overlay || {};
  set('--epx-dmn-overlay-scrim',       ov.scrim);
  set('--epx-dmn-overlay-shadow',      ov.shadow);
  set('--epx-dmn-overlay-menu-shadow', ov.menuShadow);
  set('--epx-dmn-logic-chip-bg',       ov.logicChipBg);
  set('--epx-dmn-logic-chip-text',     ov.logicChipText);
  set('--epx-dmn-logic-chip-hover',    ov.logicChipHover);
}
